import React, { useState } from "react";

import type { Task } from "@/core/types";
import { ConsentOnLogIn } from "@/component-library/ConsentOnLogIn/ConsentOnLogIn";
import { ConsentToACP } from "@/component-library/ConsentToACP/ConsentToACP";
import { ConsentedPolicyEnum } from "@/core/constants";
import { TaskTypeEnum } from "@/core/constants";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import { usePolicyMetadataQuery } from "@/lib/queries/usePolicyMetadata";

import Spinner from "../../UI/Spinner";
import { useAppendConsentMutation } from "@/lib/mutations/useAppendConsentMutation";

type Props = {
  task: Task;
  onTaskCompleted: (task: Task) => void;
};

export function UpdatedPoliciesConsent({ onTaskCompleted, task }: Props) {
  const policyType = task.policy_type;
  const { mutateAsync: appendConsent } = useAppendConsentMutation();

  const [keyId, setKeyId] = useState(0);
  const { data: policyMetadata, isLoading } = usePolicyMetadataQuery();

  const onGiveConsent = async (response: boolean = true) => {
    if (!policyMetadata) return;

    const policy = policyMetadata[policyType];
    const consentPayload = getConsentPayload({
      consentResponse: response,
      consentType: ConsentedPolicyEnum[policyType],
      patientId: task.target_patient_id,
      policyMetadata,
      policyVersion: policy.current_version,
    });

    await appendConsent(consentPayload);

    onTaskCompleted(task);
    setKeyId((prev) => prev + 1);
  };

  if (isLoading || !policyMetadata || !task) return <Spinner />;

  switch (policyType) {
    case "CONSENT_TO_TREATMENT":
    case "HIPAA":
    case "PRIVACY":
    case "TERMS_AND_CONDITIONS":
      return (
        <ConsentOnLogIn
          key={`ConsentOnLogInKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
          subTitle={getConsentCopy(task)}
          title={task.label}
        />
      );

    case "ADVANCED_CARE_PROGRAM":
      return (
        <ConsentToACP
          key={`ConsentToACPKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
        />
      );

    default:
      return <Spinner />; // better than nothing
  }
}

function getConsentCopy(task: Task) {
  const { target_patient_name, task_type } = task;

  switch (task_type) {
    case TaskTypeEnum.UpdatedPolicies:
      return "We've updated the terms of our policies. By selecting “Agree & Continue”, you confirm that you have read and agree to the following policies.";

    case TaskTypeEnum.UpdatedConsentRequired:
      return "Our records indicate that you recently turned 18. By selecting “Agree & Continue”, you confirm that you have read and agree to the following policies.";

    case TaskTypeEnum.UpdatedChildConsent:
      return `We've updated the terms of our policies. By selecting “Agree & Continue"”, you confirm that you have read and agree to the following policies on behalf of your child ${target_patient_name}.`;

    default:
      return "";
  }
}
